import { useState, useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { MapPin, TrendingUp, Package, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const Zones = () => {
  const { token } = useAuth();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API}/stats/zones`, { headers });
      setZones(res.data);
    } catch {
      toast.error("Erreur chargement des zones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
    // eslint-disable-next-line
  }, []);

  const handleResync = async () => {
    if (resyncing) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/stats/zones/resync`, {}, { headers });
      toast.success(res.data.message || "Carte resynchronisée");
      await fetchZones();
    } catch {
      toast.error("Échec de la resynchronisation");
    } finally {
      setResyncing(false);
    }
  };

  const maxCount = Math.max(...zones.map(z => z.count), 1);
  const totalLivraisons = zones.reduce((s, z) => s + z.count, 0);
  const totalCA = zones.reduce((s, z) => s + z.ca, 0);
  const validZones = zones.filter(z => z.lat && z.lng);

  const getColor = (count) => {
    const ratio = count / maxCount;
    if (ratio > 0.7) return "#ef4444";
    if (ratio > 0.4) return "#f97316";
    if (ratio > 0.2) return "#eab308";
    return "#22c55e";
  };

  const getRadius = (count) => Math.max(8, Math.min(40, (count / maxCount) * 40));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="zones-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Carte des ventes</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Visualisez vos zones de livraison les plus actives</p>
        </div>
        <Button
          onClick={handleResync}
          disabled={resyncing}
          className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
          data-testid="resync-zones-btn"
          title="Reconnecte tous les clients validés à la carte : normalise villes/CP et regéocode les manquants."
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${resyncing ? "animate-spin" : ""}`} />
          {resyncing ? "Resynchronisation…" : "Resynchroniser la carte"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Zones actives</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{zones.length}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-sm">Livraisons totales</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totalLivraisons}</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">CA total</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totalCA.toFixed(0)} €</p>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-3 sm:p-4 border-b flex flex-wrap items-center gap-2 sm:gap-4">
          <h2 className="font-semibold text-slate-700 text-sm sm:text-base">Carte de chaleur</h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-500 sm:ml-auto">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span> Faible</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-500 inline-block"></span> Moyen</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-500 inline-block"></span> Élevé</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> Très élevé</span>
          </div>
        </div>
        <div className="h-[350px] sm:h-[500px]">
          {validZones.length > 0 ? (
            <MapContainer
              center={[50.5, 4.3]}
              zoom={8}
              style={{ height: "100%", width: "100%" }}
              data-testid="zones-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {validZones.map((zone, i) => (
                <CircleMarker
                  key={i}
                  center={[zone.lat, zone.lng]}
                  radius={getRadius(zone.count)}
                  fillColor={getColor(zone.count)}
                  color={getColor(zone.count)}
                  weight={1}
                  opacity={0.8}
                  fillOpacity={0.5}
                >
                  <Tooltip>
                    <div className="text-xs">
                      <p className="font-bold">{zone.ville} {zone.code_postal}</p>
                      <p>{zone.count} livraison{zone.count > 1 ? "s" : ""}</p>
                      <p>{zone.ca.toFixed(0)} € CA</p>
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 p-4">
              <div className="text-center">
                <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucune donnée de livraison géolocalisée</p>
                <p className="text-sm mt-1">Cliquez sur « Resynchroniser la carte » pour reconnecter les clients.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top zones table */}
      {zones.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-3 sm:p-4 border-b">
            <h2 className="font-semibold text-slate-700 text-sm sm:text-base">Top zones par volume</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 sm:px-4 py-3 text-slate-500 font-medium">#</th>
                  <th className="text-left px-3 sm:px-4 py-3 text-slate-500 font-medium">Ville</th>
                  <th className="text-left px-3 sm:px-4 py-3 text-slate-500 font-medium hidden sm:table-cell">Code postal</th>
                  <th className="text-right px-3 sm:px-4 py-3 text-slate-500 font-medium">Livr.</th>
                  <th className="text-right px-3 sm:px-4 py-3 text-slate-500 font-medium">CA</th>
                  <th className="text-right px-3 sm:px-4 py-3 text-slate-500 font-medium hidden md:table-cell">Moy/livr.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {zones.slice(0, 30).map((zone, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 sm:px-4 py-3 text-slate-400 font-medium">{i + 1}</td>
                    <td className="px-3 sm:px-4 py-3 font-medium text-slate-800">
                      {zone.ville || "—"}
                      <span className="sm:hidden block text-xs text-slate-400">{zone.code_postal || ""}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-slate-600 hidden sm:table-cell">{zone.code_postal || "—"}</td>
                    <td className="px-3 sm:px-4 py-3 text-right">
                      <span className="font-semibold">{zone.count}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-right text-green-600 font-medium">{zone.ca.toFixed(0)} €</td>
                    <td className="px-3 sm:px-4 py-3 text-right text-slate-500 hidden md:table-cell">{zone.count > 0 ? (zone.ca / zone.count).toFixed(0) : 0} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zones;
